import type { Brand } from './brand';

/**
 * The PRESENCE axis — what the frozen read plane reported about the requested subject.
 *
 * DERIVED FROM, VERBATIM (no addition, no rename, no widening):
 *   backend/contracts/src/main/java/org/rescue/contract/api/readmodel/
 *     CapabilityDefinitionReadResultV1.java
 *       enum Status { AVAILABLE, UNKNOWN, UNAVAILABLE }
 *   blob 9ebdb136dca12c207d127a006951dc4eca82e5cf
 *
 * THREE MEMBERS ARE THE WHOLE VOCABULARY. There is no fourth presence value.
 *
 * IN PARTICULAR THERE IS NO `ERROR` AND NO `EMPTY` ON THIS AXIS.
 *   The frozen contract defines neither. FE-1 Stage Work Item §7.5 states that
 *   `Loading` and `Empty` are NOT contract states and MUST NOT be substituted for
 *   `UNKNOWN`, and §6.4 states that `UNAVAILABLE` is a distinct reader-failure
 *   state. `Loading`, `Empty` and `Error` live on a DIFFERENT axis with a
 *   different brand (`./condition.ts`), so substituting one for a presence value
 *   is a compile error, not a code-review question.
 *
 * WHAT EACH MEMBER MEANS — taken from the frozen contract's own documentation and
 * its authoritative read seam, not inferred here:
 *
 *   AVAILABLE    "means only that snapshot data was retrieved, never that the
 *                capability is operationally available or ready."
 *                AVAILABLE REQUIRES a matching snapshot; absent or unavailable
 *                data must not carry a snapshot. `./observation.ts` enforces this
 *                structurally rather than by convention.
 *   UNKNOWN      "is normal data absence, not an error." On this read plane an
 *                EMPTY source maps to UNKNOWN — not to an error and not to an
 *                empty state.
 *   UNAVAILABLE  "means a functioning reader could not supply a trustworthy
 *                response." A null reader, a null source, a capabilityId mismatch
 *                and a reader RuntimeException all map here, with NO diagnostic
 *                published.
 */

/** The subject's data was retrieved. NOT an operational-availability claim. */
export type PresenceAvailable = Brand<'AVAILABLE', 'PresenceAxis'>;

/** Normal data absence. NOT an error, NOT empty, NOT a default. */
export type PresenceUnknown = Brand<'UNKNOWN', 'PresenceAxis'>;

/** A functioning reader could not supply a trustworthy response. NOT fabricated data. */
export type PresenceUnavailable = Brand<'UNAVAILABLE', 'PresenceAxis'>;

/** The closed presence vocabulary. */
export type Presence = PresenceAvailable | PresenceUnknown | PresenceUnavailable;

/**
 * The three presence values. This object is the only construction site, so the
 * vocabulary cannot drift by a literal written somewhere else.
 */
export const Presence = Object.freeze({
  AVAILABLE: 'AVAILABLE' as PresenceAvailable,
  UNKNOWN: 'UNKNOWN' as PresenceUnknown,
  UNAVAILABLE: 'UNAVAILABLE' as PresenceUnavailable,
});
